import React from 'react';
import { IonIcon, IonText } from '@ionic/react';
import ArrowLeft from 'assets/icons/arrow-left.svg';
import Close from 'assets/icons/close.svg';
import './HkModal.scss';

interface HkModalHeaderProps {
  title: string;
  onClose: () => void;
  onBack?: () => void;
}

export const slugifyModalTitle = (title: string) =>
  `hk-modal-title-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

/**
 * Standalone header piece (title + close icon), split out of `HkModalContent`
 * so a multi-step flow sharing one outer IonModal (e.g. InventoryAddProduct's
 * landing → manual-form steps) can render it once, unconditionally, while
 * only the content/footer below it change per step.
 *
 * `onBack` is optional — when provided, a left-arrow renders before the title
 * and the header switches to a centered-title layout (see
 * `hk-modal-header--with-back`); other consumers of this shared header are
 * unaffected.
 */
const HkModalHeader: React.FC<HkModalHeaderProps> = ({
  title,
  onClose,
  onBack
}) => {
  const titleId = slugifyModalTitle(title);
  return (
    <div
      className={`hk-modal-header${
        onBack ? ' hk-modal-header--with-back' : ''
      }`}
    >
      {/*
        Buttons, not bare clickable IonIcons: `ion-icon` renders as role="img"
        and is not focusable, and HkModal sets backdropDismiss={false} — so
        these were mouse-only, leaving Esc as the only way off the screen.
        Native <button> rather than IonButton (CLAUDE.md §2's documented
        exception): these are bare 24px icon affordances inside a plain flex
        header div, and IonButton's own min-height/padding/ripple chrome would
        have to be zeroed out custom-prop by custom-prop to match. The reset
        lives alongside the existing sizing rules in HkModal.scss.
      */}
      {!!onBack && (
        <button
          type="button"
          className="hk-modal-header-back"
          aria-label="Back"
          onClick={onBack}
        >
          <IonIcon icon={ArrowLeft} aria-hidden="true" />
        </button>
      )}
      <IonText id={titleId} className="hk-modal-header-title">
        {title}
      </IonText>
      <button
        type="button"
        className="hk-modal-header-close"
        aria-label="Close"
        onClick={onClose}
      >
        <IonIcon icon={Close} aria-hidden="true" />
      </button>
    </div>
  );
};

export default HkModalHeader;
